import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import type React from 'react';
import { useMemo } from 'react';
import { computeSentenceDiff, type DiffChange } from '../utils/sentenceDiff';

interface RichDiffViewerProps {
  oldText: string;
  newText: string;
  onApply: (newOldText: string, newNewText: string) => void;
}

type DiffBlock =
  | { type: 'unchanged'; text: string; id?: string }
  | { type: 'added'; text: string; id?: string }
  | { type: 'removed'; text: string; id?: string }
  | { type: 'replacement'; oldText: string; newText: string; id?: string };

const RichDiffViewer: React.FC<RichDiffViewerProps> = ({ oldText, newText, onApply }) => {
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';
  const removedBg = isDark ? 'rgba(244, 67, 54, 0.15)' : '#ffeef0';
  const removedColor = isDark ? '#ff8a80' : '#b71c1c';
  const addedBg = isDark ? 'rgba(76, 175, 80, 0.15)' : '#e6ffed';
  const addedColor = isDark ? '#81c784' : '#1b5e20';

  // 1. Compute and Group Diffs
  const blocks = useMemo(() => {
    if (!oldText && !newText) {
      return [];
    }
    // Handle edge cases where one is empty
    if (!oldText) {
      return [{ type: 'added', text: newText }] as DiffBlock[];
    }
    if (!newText) {
      return [{ type: 'removed', text: oldText }] as DiffBlock[];
    }

    let rawDiffs: DiffChange[] = [];
    try {
      rawDiffs = computeSentenceDiff(oldText, newText);
    } catch (_e) {
      // Fallback: treat as total replacement
      return [{ type: 'replacement', oldText, newText }] as DiffBlock[];
    }

    const grouped: DiffBlock[] = [];
    let i = 0;
    while (i < rawDiffs.length) {
      const current = rawDiffs[i];
      const next = rawDiffs[i + 1];

      if (!current) {
        i++;
        continue;
      }

      if (current.type === 'removed' && next?.type === 'added') {
        grouped.push({
          type: 'replacement',
          oldText: current.text,
          newText: next.text,
        });
        i += 2;
      } else {
        grouped.push({
          type: current.type,
          text: current.text,
        } as DiffBlock);
        i++;
      }
    }
    return grouped;
  }, [oldText, newText]);

  const handleAction = (index: number, action: 'accept' | 'reject') => {
    // Reconstruct strings
    let newOld = '';
    let newNew = '';

    blocks.forEach((block, idx) => {
      if (idx === index) {
        if (action === 'accept') {
          // Accept: Apply change to Old. New stays same (effectively matches now).
          if (block.type === 'added') {
            newOld += block.text;
            newNew += block.text;
          } else if (block.type === 'removed') {
            // Exclude from Old. Exclude from New.
          } else if (block.type === 'replacement') {
            newOld += block.newText;
            newNew += block.newText;
          }
        } else {
          // Reject: Revert change in New. Old stays same.
          if (block.type === 'added') {
            // Exclude from New. Exclude from Old (was never there).
          } else if (block.type === 'removed') {
            // Include in New (restore). Include in Old (was there).
            newOld += block.text;
            newNew += block.text;
          } else if (block.type === 'replacement') {
            // Use Old in New. Use Old in Old.
            newOld += block.oldText;
            newNew += block.oldText;
          }
        }
      } else {
        // Other blocks - preserve "current state"
        // For Old:
        if (block.type === 'unchanged') {
          newOld += block.text;
        } else if (block.type === 'removed') {
          newOld += block.text;
        } else if (block.type === 'replacement') {
          newOld += block.oldText;
        }
        // Added is not in Old.

        // For New:
        if (block.type === 'unchanged') {
          newNew += block.text;
        } else if (block.type === 'added') {
          newNew += block.text;
        } else if (block.type === 'replacement') {
          newNew += block.newText;
        }
        // Removed is not in New.
      }
    });

    onApply(newOld, newNew);
  };

  return (
    <Box sx={{ fontFamily: 'inherit', lineHeight: 1.6, color: 'text.primary' }}>
      {blocks.map((block, index) => {
        if (block.type === 'unchanged') {
          return (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: Render order is fixed by diff algorithm
              key={index}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.text) }}
            />
          );
        }

        return (
          <Box
            component="span"
            // biome-ignore lint/suspicious/noArrayIndexKey: Render order is fixed by diff algorithm
            key={index}
            sx={{
              display: 'inline-block',
              mx: 0.5,
              my: 0.5,
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              p: 0.5,
              verticalAlign: 'middle',
              position: 'relative',
              transition: 'all 0.2s',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -12,
                left: -12,
                display: 'flex',
                gap: 0.5,
                zIndex: 10,
                bgcolor: 'background.paper',
                borderRadius: 10,
                boxShadow: 1,
              }}
            >
              <Tooltip title="Accept Change">
                <IconButton
                  size="small"
                  onClick={() => handleAction(index, 'accept')}
                  color="success"
                  sx={{ p: 0.5 }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject Change">
                <IconButton
                  size="small"
                  onClick={() => handleAction(index, 'reject')}
                  color="error"
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {block.type === 'replacement' && (
              <>
                <Box
                  component="span"
                  sx={{
                    bgcolor: removedBg,
                    color: removedColor,
                    textDecoration: 'line-through',
                    mr: 1,
                    opacity: 0.7,
                  }}
                >
                  {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized */}
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.oldText) }} />
                </Box>
                <Box component="span" sx={{ bgcolor: addedBg, color: addedColor }}>
                  {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized */}
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.newText) }} />
                </Box>
              </>
            )}
            {block.type === 'added' && (
              <Box component="span" sx={{ bgcolor: addedBg, color: addedColor }}>
                <span
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.text) }}
                />
              </Box>
            )}
            {block.type === 'removed' && (
              <Box
                component="span"
                sx={{ bgcolor: removedBg, color: removedColor, textDecoration: 'line-through' }}
              >
                <span
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.text) }}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default RichDiffViewer;
